import { ReactElement, useContext, useEffect, useState } from "react";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import {
  selectIsRequiredLoading,
  selectIsSuccessful,
} from "modules/systemCheck/redux/selectors";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { useLobbyCheck } from "modules/event/prechecks/lobby";
import { PrecheckStatus } from "modules/event/prechecks";
import { onBoard } from "modules/appSetting/redux/appSettingSlice";
import { useHistory } from "react-router";
import { selectCurrentEvent } from "modules/event/selectors";
import useGuestLobbyPage from "modules/lobby/hooks/useGuestLobbyPage";
import { useDevicePermissions } from "modules/audioVideo/hooks";
import {
  PermissionState,
  selectAudioPermissionsState,
  selectHasAudioPermissions,
  selectHasVideoPermissions,
  selectVideoPermissionsState,
} from "modules/deviceInfo";
import { GeartestContext } from "modules/micCamCheck/types";
import { useDeviceDetection } from "modules/systemCheck/hooks/useDeviceDetection";
import { setIsSystemCheckCompleted } from "../../redux/systemCheckSlice";
import { LobbyControls } from "../LobbyControls";

interface Props {
  context: GeartestContext;
  retrySystemCheck: () => void;
  audioPermissionState: PermissionState;
  videoPermissionState: PermissionState;
}

const getJoinEventWithoutDeviceText = (mic: boolean, cam: boolean) => {
  if (!mic && !cam) {
    return "micCamCheck:join.event.without.mic.and.cam";
  }
  if (cam && !mic) {
    return "micCamCheck:join.event.without.mic";
  }
  if (!cam && mic) {
    return "micCamCheck:join.event.without.cam";
  }

  return "";
};

const ResultControls = ({
  context,
  retrySystemCheck,
  audioPermissionState,
  videoPermissionState,
}: Props): ReactElement => {
  const isPermissionsPending =
    audioPermissionState === PermissionState.IDLE ||
    videoPermissionState === PermissionState.IDLE;
  const { track } = useContext(TRACKING_CONTEXT);
  const { t } = useI18n(["common", "micCamCheck", "errorPage"]);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { noMic, noCam } = useDeviceDetection();
  const [isRetryButtonLoading, setIsRetryButtonLoading] = useState(false);
  const [isInitialPermissionsCheckDone, setIsInitialPermissionsCheckDone] =
    useState(false);
  const isSuccessful = useSelector(selectIsSuccessful);
  const isRequiredLoading = useSelector(selectIsRequiredLoading);
  const hasAudioPermissions = useSelector(selectHasAudioPermissions);
  const hasVideoPermissions = useSelector(selectHasVideoPermissions);
  const audioPermissionsState = useSelector(selectAudioPermissionsState);
  const videoPermissionsState = useSelector(selectVideoPermissionsState);

  const [autoJoin, setAutoJoin] = useState(false);
  const event = useSelector(selectCurrentEvent);
  const { status: lobbyStatus } = useLobbyCheck();
  const { event: eventCode } = useQueryParams();
  const { isApproved } = useGuestLobbyPage(event);

  const handleNext = () => {
    if (!eventCode) return;

    dispatch(onBoard(true));
    history.push(`/e/${eventCode.toString()}`);
  };

  const handleJoinEvent = () => {
    if (event?.isLobbyActive && !isApproved) return;

    dispatch(setIsSystemCheckCompleted(true));
    handleNext();
  };

  const handleClickDisallow = (e: React.SyntheticEvent) => {
    e.preventDefault();

    dispatch(setIsSystemCheckCompleted(true));
    track(Events.SYSTEM_CHECK_EJECTED, {
      audioPermissions: audioPermissionsState,
      videoPermissions: videoPermissionsState,
    });
    handleNext();
  };

  const { checkPermissions } = useDevicePermissions({
    onGranted() {
      retrySystemCheck();

      setIsRetryButtonLoading(false);
      if (!isInitialPermissionsCheckDone) {
        setIsInitialPermissionsCheckDone(true);
      }
    },
    onDenied() {
      retrySystemCheck();

      setIsRetryButtonLoading(false);
      if (!isInitialPermissionsCheckDone) {
        setIsInitialPermissionsCheckDone(true);
      }
    },
  });

  const handleRetryButtonClick = () => {
    if (audioPermissionState === "idle" || videoPermissionState === "idle") {
      retrySystemCheck();
    } else {
      setIsRetryButtonLoading(true);
    }
    checkPermissions();
  };

  const handleRefreshPageButtonClick = () => {
    window.location.reload();
  };

  const toggleAutoJoin = () => {
    setAutoJoin(!autoJoin);
  };

  const showLobbyOrJoinWithoutMicCam = (hasMic: boolean, hasCam: boolean) => {
    if (lobbyStatus === PrecheckStatus.PENDING) {
      return null;
    }

    if (!isApproved && lobbyStatus === PrecheckStatus.FAIL) {
      return (
        <LobbyControls
          message={event?.lobbyWelcomeMessage}
          autoJoinDisabled={isRequiredLoading}
          autoJoin={autoJoin}
          toggleAutoJoin={toggleAutoJoin}
        />
      );
    }

    return (
      <Container top="sm">
        <Button
          fullWidth
          variant="text"
          color="blue"
          size="md"
          id="join-event-with-mic-cam_text-link-cta-join-event-without-camera-microphone"
          onClick={handleClickDisallow}
        >
          {t(getJoinEventWithoutDeviceText(hasMic, hasCam))}
        </Button>
      </Container>
    );
  };

  useEffect(() => {
    if (autoJoin && isApproved) {
      handleJoinEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoJoin, isApproved]);

  const disableRefreshPageButton = isPermissionsPending || isRetryButtonLoading;

  return (
    <div data-testid="result-controls">
      {(() => {
        switch (context) {
          case GeartestContext.preEvent:
            if (!isSuccessful && !isRequiredLoading) {
              return (
                <Container top="sm">
                  <Button
                    fullWidth
                    variant="secondary"
                    color="dark"
                    onClick={handleRetryButtonClick}
                    disabled={isRetryButtonLoading}
                    data-testid="retry-page-btn"
                    autoFocus
                  >
                    {t("micCamCheck:retry.system.check")}
                  </Button>
                </Container>
              );
            }

            if (!hasAudioPermissions || !hasVideoPermissions) {
              return (
                <>
                  <Container top="sm">
                    <Button
                      fullWidth
                      variant="secondary"
                      color="dark"
                      onClick={handleRefreshPageButtonClick}
                      data-testid="refresh-page-btn"
                      loading={disableRefreshPageButton}
                      autoFocus
                    >
                      {t("errorPage:refresh.page")}
                    </Button>
                  </Container>
                  {showLobbyOrJoinWithoutMicCam(
                    hasAudioPermissions,
                    hasVideoPermissions,
                  )}
                </>
              );
            }

            if (noMic || noCam) {
              return (
                <>
                  <Container top="sm">
                    <Button
                      fullWidth
                      variant="secondary"
                      color="dark"
                      onClick={handleRefreshPageButtonClick}
                      data-testid="refresh-page-btn"
                      disabled={disableRefreshPageButton}
                      autoFocus
                    >
                      {disableRefreshPageButton
                        ? t("micCamCheck:waiting.for.permissions")
                        : t("errorPage:refresh.page")}
                    </Button>
                  </Container>
                  {showLobbyOrJoinWithoutMicCam(!noMic, !noCam)}
                </>
              );
            }

            if (lobbyStatus === PrecheckStatus.PENDING) {
              return (
                <Container top="lg">
                  <Button
                    fullWidth
                    variant="primary"
                    color="blue"
                    onClick={handleJoinEvent}
                    disabled
                    data-testid="join-event-btn"
                  >
                    {t("micCamCheck:join.event")}
                  </Button>
                </Container>
              );
            }

            if (!isApproved && lobbyStatus === PrecheckStatus.FAIL && event) {
              return (
                <LobbyControls
                  message={event.lobbyWelcomeMessage}
                  autoJoinDisabled={isRequiredLoading}
                  autoJoin={autoJoin}
                  toggleAutoJoin={toggleAutoJoin}
                />
              );
            }

            return (
              <Container top="lg">
                <Button
                  fullWidth
                  variant="primary"
                  color="blue"
                  onClick={handleJoinEvent}
                  disabled={isRetryButtonLoading || isRequiredLoading}
                  data-testid="join-event-btn"
                >
                  {t("micCamCheck:join.event")}
                </Button>
              </Container>
            );

          case GeartestContext.public:
            if (!isSuccessful && !isRequiredLoading) {
              return (
                <Container top="sm">
                  <Button
                    fullWidth
                    variant="primary"
                    color="blue"
                    onClick={handleRetryButtonClick}
                    disabled={isRetryButtonLoading}
                    data-testid="retry-page-btn"
                    autoFocus
                  >
                    {t("micCamCheck:retry.system.check")}
                  </Button>
                </Container>
              );
            }

            return (
              <Container top="sm">
                <Button
                  fullWidth
                  variant="primary"
                  color="blue"
                  onClick={handleRefreshPageButtonClick}
                  data-testid="refresh-page-btn"
                  disabled={isRequiredLoading}
                  autoFocus
                >
                  {t("errorPage:refresh.page")}
                </Button>
              </Container>
            );

          default:
            return null;
        }
      })()}
    </div>
  );
};

export default ResultControls;
